import dayjs from 'dayjs';
import { useState } from 'react';
import { DEFAULT_ASSEMBLY_COMPONENT_CONFIG } from 'src/constants';
import { handleFiltersForComponents } from 'src/features/assemblies/editAssemblyPage/EditAssemblyPage.helper';
import { getInvalidBackups } from 'src/features/locations/scheduleComponentPage/ScheduleComponentPage.helper';
import { useGetComponentTypes, useModal } from 'src/hooks';
import { ComponentTypes, IComponent } from 'src/interfaces';
import { lang } from 'src/utils';
import { getBackgroundVideoDuration } from 'src/utils/file-types';
import { uid } from 'uid';

import {
  IDefaultSection,
  IGridSection,
  IScheduledSectionComponent,
} from '../../../ScheduleComponentPage.interface';
import { isAllowedComponentType } from '../../componentAccordion/ComponentAccordion.helper';

const defaultReScheduleComponentIds = {
  assemblyComponentId: undefined,
  componentId: -1,
};

export const useComponentListModal = (
  layoutTypeId: number,
  selectedSection: IGridSection | IDefaultSection,
  onUpdateScreensGridSectionsWhenSectionChanges: (
    section: IGridSection | IDefaultSection,
  ) => void,
  selectedComponentCallback?: () => void,
) => {
  const currentDate = dayjs().add(1, 'day').format('YYYY-MM-DD');

  const [currentScheduledComponentIdsMap, setCurrentScheduledComponentIdsMap] =
    useState<{
      assemblyComponentId: string | undefined;
      componentId: number;
    }>(defaultReScheduleComponentIds);
  const [reScheduledComponent, setReScheduledComponent] =
    useState<IComponent>();

  const mainComponentListModal = useModal();
  const removingInvalidBackupsConfirmationModal = useModal();

  const { data: componentTypes } = useGetComponentTypes([], {
    enabled: mainComponentListModal.open,
  });

  const filterParameters = handleFiltersForComponents(
    currentDate,
    layoutTypeId,
    componentTypes,
    currentScheduledComponentIdsMap.componentId,
    {
      width: selectedSection.width!,
      height: selectedSection.height!,
      config: selectedSection.config,
    },
  );

  const onCloseComponentModal = () => {
    setCurrentScheduledComponentIdsMap(defaultReScheduleComponentIds);
    mainComponentListModal.onCloseModal();
  };

  const onOpenComponentModal = () => {
    mainComponentListModal.onOpenModal();
  };

  const onReScheduleComponent = (
    selectComponent: IScheduledSectionComponent,
  ) => {
    setCurrentScheduledComponentIdsMap({
      assemblyComponentId: selectComponent.uid,
      componentId: selectComponent.component?.id as number,
    });
    mainComponentListModal.onOpenModal();
  };

  const onAddAdditionalComponent = () => {
    mainComponentListModal.onOpenModal();
  };

  const handleUpdateCurrentSection = (
    component: IComponent,
    invalidBackupIds: string[] = [],
  ) => {
    onUpdateScreensGridSectionsWhenSectionChanges({
      ...selectedSection,
      components: (
        selectedSection.components as IScheduledSectionComponent[]
      ).map((item) => {
        if (item.uid === currentScheduledComponentIdsMap.assemblyComponentId) {
          const updatedComponent: IScheduledSectionComponent = {
            ...item,
            component_id: component.id,
            component,
          };

          if (!isAllowedComponentType(updatedComponent)) {
            updatedComponent.allow_overrides = false;
          }

          return {
            ...updatedComponent,
            scheduleStartDate: null,
            scheduleEndDate: component.end_date,
            backups:
              component.component_type?.name !==
              ComponentTypes.PROMOTIONAL_SPACE
                ? []
                : item.backups?.filter(
                    (backup) => !invalidBackupIds.includes(backup.uid),
                  ),
          };
        }

        return item;
      }),
    });

    onCloseComponentModal();
    removingInvalidBackupsConfirmationModal.onCloseModal();
  };

  const replacePreviousScheduleComponent = (component: IComponent) => {
    const currentScheduledComponent = (
      selectedSection.components as IScheduledSectionComponent[]
    ).find(
      (item) =>
        item.uid === currentScheduledComponentIdsMap.assemblyComponentId,
    );
    const invalidBackupIds = getInvalidBackups(
      component.end_date,
      currentScheduledComponent?.backups,
    );

    if (invalidBackupIds.length) {
      setReScheduledComponent(component);
      removingInvalidBackupsConfirmationModal.onOpenModal(() => {
        handleUpdateCurrentSection(component, invalidBackupIds);
      });
    } else {
      handleUpdateCurrentSection(component);
    }
  };

  const addNewScheduleComponent = async (component: IComponent) => {
    const { components = [] } = selectedSection;
    const updatedDuration = await getBackgroundVideoDuration(
      component?.config?.background,
      DEFAULT_ASSEMBLY_COMPONENT_CONFIG.duration,
    );

    onUpdateScreensGridSectionsWhenSectionChanges({
      ...selectedSection,
      components: [
        ...components,
        {
          uid: uid(),
          id: 0,
          assembly_id: -1,
          component_id: component.id,
          scheduleStartDate: null,
          scheduleEndDate: component.end_date,
          rotation_order: -1,
          backup_order: 0,
          config: {
            ...DEFAULT_ASSEMBLY_COMPONENT_CONFIG,
            duration: updatedDuration,
          },
          component,
        },
      ],
    });
  };

  const onSelectedComponent = (component: IComponent) => {
    if (currentScheduledComponentIdsMap.assemblyComponentId) {
      replacePreviousScheduleComponent(component);
    } else {
      addNewScheduleComponent(component);
      onCloseComponentModal();
    }

    selectedComponentCallback?.();
  };

  const handleCloseInvalidBackupsConfirmationModal = () => {
    handleUpdateCurrentSection(reScheduledComponent as IComponent);
  };

  return {
    showComponentListModal: mainComponentListModal.open && !!componentTypes,
    filterParameters,
    modalTitle: currentScheduledComponentIdsMap.assemblyComponentId
      ? lang.scheduleComponent.swapComponent
      : undefined,
    removingInvalidBackupsConfirmationModal,
    onCloseComponentModal,
    onSelectedComponent,
    onOpenComponentModal,
    onReScheduleComponent,
    onAddAdditionalComponent,
    handleCloseInvalidBackupsConfirmationModal,
  };
};
